import {useQuery} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {Box, SimpleGrid, Stack, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import staticURL from 'lib/staticURL';
import OmniboxFilterTabs, {type Tab} from 'omnibox/OmniboxFilterTabs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import PlainSearchInput, {BorderRadius} from 'ui-library/PlainSearchInput';

import {ListExtensions, ListExtensionsVariables} from '../__generated/ListExtensions';
import {LIST_EXTENSIONS_QUERY} from '../ExtensionQueries';
import ExtensionCard from './ExtensionCard';
import {type Extension, EXTENSION_TYPE_LABELS} from './extensionConstants';
import ExtensionDetails from './ExtensionDetails';
import InstallConfigStep from './InstallConfigStep';

/** Plural label for the active tab — matches tab labels ("MCPs", "Scientific Models", …). */
function extensionTypePluralLabel(extensionType: ExtensionType): string {
  return `${EXTENSION_TYPE_LABELS[extensionType]}s`;
}

function BrowseTitle({activeTab}: {activeTab: ExtensionType}): JSX.Element {
  return (
    <span className={css(styles.browseTitle)}>
      Browse
      <span className={css(styles.browseTitleCategory)}>{extensionTypePluralLabel(activeTab)}</span>
    </span>
  );
}

function BackButton({onBack}: {onBack: () => void}): JSX.Element {
  return (
    <span className={css(styles.headerBack)}>
      <Button
        text="Back"
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        size={ButtonSize.SMALL}
        icon="arrow-left"
        onClick={onBack}
        className={css(styles.backButtonIconSpacing)}
      />
    </span>
  );
}

interface ContentStoreModalProps {
  onRequestClose: () => void;
  onRequestSubmit?: (result: unknown) => void | Promise<void>;
  onInstall?: (installedExtension: Extension) => void;
  modalKey?: string;
  extensionType?: ExtensionType;
}

const CONTENT_STORE_MODAL_WIDTH = 650;

function buildTabConfig(): Array<Tab<ExtensionType>> {
  const tabs: Array<Tab<ExtensionType>> = [
    {id: ExtensionType.MCP_SERVER, label: extensionTypePluralLabel(ExtensionType.MCP_SERVER)},
    {id: ExtensionType.MODEL, label: extensionTypePluralLabel(ExtensionType.MODEL)},
  ];
  if (BenchlingConfig.ENABLE_EXTENSION_TYPE_AGENT_SKILLS) {
    tabs.push({id: ExtensionType.AGENT_SKILL, label: extensionTypePluralLabel(ExtensionType.AGENT_SKILL)});
  }
  return tabs;
}

export default function ContentStoreModal({
  onRequestClose,
  onRequestSubmit,
  onInstall,
  extensionType,
}: ContentStoreModalProps): JSX.Element {
  const tabConfig = useMemo(() => buildTabConfig(), []);
  const defaultTab = extensionType ?? tabConfig[0]?.id ?? ExtensionType.MCP_SERVER;
  const [activeTab, setActiveTab] = useState<ExtensionType>(defaultTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExtension, setSelectedExtension] = useState<Extension | null>(null);
  const [installingExtension, setInstallingExtension] = useState<Extension | null>(null);

  const variables: ListExtensionsVariables = {extensionType: activeTab};
  const {data, loading, error} = useQuery<ListExtensions, ListExtensionsVariables>(LIST_EXTENSIONS_QUERY, {
    variables,
    fetchPolicy: 'network-only',
  });

  const extensions = useMemo(
    () => data?.extensionRegistryListing?.items ?? [],
    [data?.extensionRegistryListing?.items]
  );

  const filteredExtensions = useMemo(() => {
    if (!searchQuery) {
      return extensions;
    }
    const query = searchQuery.toLowerCase();
    return extensions.filter((ext) => {
      const name = ext.name ?? ext.id;
      const tagLine = ext.tagLine ?? '';
      return name.toLowerCase().includes(query) || tagLine.toLowerCase().includes(query);
    });
  }, [extensions, searchQuery]);

  const handleTabChange = useCallback((tab: ExtensionType) => {
    setActiveTab(tab);
    setSearchQuery('');
  }, []);

  const handleSelectExtension = useCallback((ext: Extension) => {
    setSelectedExtension(ext);
  }, []);

  const handleBackToList = useCallback(() => {
    setSelectedExtension(null);
  }, []);

  const handleBeginInstallConfig = useCallback(() => {
    if (selectedExtension != null) {
      setInstallingExtension(selectedExtension);
    }
  }, [selectedExtension]);

  const handleBackToDetails = useCallback(() => {
    setInstallingExtension(null);
  }, []);

  const handleInstalled = useCallback(
    (installedExtension: Extension) => {
      setInstallingExtension(null);
      setSelectedExtension(null);
      onInstall?.(installedExtension);
      onRequestClose();
    },
    [onInstall, onRequestClose]
  );

  const renderContent = (): JSX.Element => {
    if (loading) {
      return <div className={css(styles.loadingBar)} />;
    }

    if (error) {
      const label = extensionTypePluralLabel(activeTab);
      return (
        <Box p="md">
          <Text>
            Error loading {label}: {error.message}
          </Text>
        </Box>
      );
    }

    if (filteredExtensions.length === 0) {
      return (
        <Box p="md">
          <Text>No {extensionTypePluralLabel(activeTab)} found</Text>
        </Box>
      );
    }

    return (
      <SimpleGrid cols={2} spacing={16} mt="sm">
        {filteredExtensions.map((ext) => (
          <ExtensionCard
            key={ext.id}
            extension={ext}
            onClick={() => {
              handleSelectExtension(ext);
            }}
          />
        ))}
      </SimpleGrid>
    );
  };

  if (installingExtension != null) {
    return (
      <Modal
        title={<BackButton onBack={handleBackToDetails} />}
        className={css(styles.modal)}
        onRequestClose={onRequestClose}
        bodyPadded={false}
      >
        <Box p="xl" pb="lg" className={css(styles.modalBodyScroll)}>
          <InstallConfigStep
            extension={installingExtension}
            onCancel={handleBackToDetails}
            onInstalled={handleInstalled}
          />
        </Box>
      </Modal>
    );
  }

  if (selectedExtension) {
    return (
      <Modal
        title={<BackButton onBack={handleBackToList} />}
        className={css(styles.modal)}
        onRequestClose={onRequestClose}
        bodyPadded={false}
      >
        <Box p="xl" pb="lg" className={css(styles.modalBodyScroll)}>
          <ExtensionDetails
            extension={selectedExtension}
            onInstall={handleInstalled}
            onBeginInstallConfig={handleBeginInstallConfig}
          />
        </Box>
      </Modal>
    );
  }

  return (
    <Modal
      title={<BrowseTitle activeTab={activeTab} />}
      className={css(styles.modal)}
      onRequestClose={onRequestClose}
      bodyPadded={false}
      showHeaderBorder={false}
    >
      <Box className={css(styles.modalBodyScroll)}>
        <Box pt="md" px="md">
          <Box className={css(styles.bannerWrapper)}>
            <img
              src={staticURL('/static/img/extensions/ui_static/Banner.png')}
              alt=""
              className={css(styles.bannerImg)}
            />
          </Box>
        </Box>

        <OmniboxFilterTabs
          tabs={tabConfig}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          className={css(styles.tabs)}
        />

        <Box px="md" pb="lg" pt="md">
          <Stack spacing="md">
            <Box className={css(styles.searchRow)}>
              <PlainSearchInput
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                onEscape={() => {
                  setSearchQuery('');
                }}
                borderRadius={BorderRadius.LARGE}
                shouldAutoSelect={false}
                className={css(styles.searchInput)}
              />
            </Box>

            <div id="extension-tab-content" role="tabpanel" aria-labelledby={`extension-tab-${activeTab}`}>
              {renderContent()}
            </div>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
}

const styles = StyleSheet.create({
  browseTitle: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 10,
  },
  browseTitleCategory: {
    fontSize: 13,
    fontWeight: 400,
    color: Color.GRAY_600,
    letterSpacing: 0.3,
  },
  modal: {
    ':nth-child(1n) .modal-dialog': {
      width: CONTENT_STORE_MODAL_WIDTH,
    },
  },
  bannerWrapper: {
    width: '100%',
    borderRadius: 4,
    overflow: 'hidden',
    flexShrink: 0,
  },
  bannerImg: {
    width: '100%',
    height: 'auto',
    display: 'block',
    verticalAlign: 'middle',
  },
  headerBack: {
    display: 'inline-flex',
    margin: 0,
  },
  backButtonIconSpacing: {
    border: 'none',
    ':nth-child(1n) .button-wrapper': {
      gap: 8,
    },
  },
  tabs: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '8px 16px',
    borderBottom: '1px solid #e9ecef',
  },
  searchRow: {
    height: 28,
    maxWidth: 300,
    display: 'flex',
  },
  searchInput: {
    height: '100%',
    flex: 1,
    minWidth: 0,
  },
  modalBodyScroll: {
    height: 600,
    overflowY: 'auto',
  },
  loadingBar: {
    height: 2,
    width: '100%',
    backgroundColor: Color.GRAY_200,
    overflow: 'hidden',
    position: 'relative',
    marginTop: 8,
    '::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: '40%',
      backgroundColor: Color.BLUE_450,
      animationName: {
        '0%': {transform: 'translateX(-100%)'},
        '100%': {transform: 'translateX(250%)'},
      },
      animationDuration: '1.2s',
      animationTimingFunction: 'ease-in-out',
      animationIterationCount: 'infinite',
    },
  },
});
